import React from 'react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, LabelList,
  BarChart, Bar, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';

// Quelle eines Datenpunkts → Label für Frontend-Anzeige
const SOURCE_LABEL = {
  customer: 'Ihre Angabe',
  estimated: 'Ihre Schätzung',
  inferred: 'aus Ihren Angaben abgeleitet',
  industry_benchmark: 'Branchen-Kennzahl (Schätzung)',
  no_data: 'keine Daten',
};
const SOURCE_BADGE = {
  customer: { cls: 'bg-emerald-100 text-emerald-700', sym: '●' },
  estimated: { cls: 'bg-amber-100 text-amber-700', sym: '~' },
  inferred: { cls: 'bg-amber-100 text-amber-700', sym: '~' },
  industry_benchmark: { cls: 'bg-blue-100 text-blue-700', sym: '⋆' },
  no_data: { cls: 'bg-gray-100 text-gray-500', sym: '–' },
};

function isEstimateSource(src) {
  return src && src !== 'customer';
}

// =============================================================================
// Reifegrad-Radar (Spinndiagramm: Ist vs Soll auf 6 Dimensionen)
// =============================================================================

export function MaturityRadar({ maturity }) {
  if (!maturity) return null;
  const dims = [
    { key: 'datenqualitaet', label: 'Datenqualität' },
    { key: 'it_infrastruktur', label: 'IT-Infrastruktur' },
    { key: 'mitarbeiter_skills', label: 'Mitarbeiter-Skills' },
    { key: 'governance', label: 'Governance' },
    { key: 'budget', label: 'Budget' },
    { key: 'usecase_pipeline', label: 'Use-Case-Pipeline' },
  ];
  const data = dims.map((d) => {
    const dim = maturity[d.key] || {};
    return { dim: d.label, ist: dim.ist ?? 0, soll: dim.soll ?? 8, _source: dim.source || 'customer', _note: dim.note || '' };
  });
  const annotations = dims
    .map((d) => ({ ...d, dim: maturity[d.key] || {} }))
    .filter((d) => isEstimateSource(d.dim.source));

  return (
    <ChartFrame title="Reifegrad — Ist vs. Soll" subtitle="Bewertung Ihres Betriebs auf sechs Dimensionen (Skala 0–10)">
      <ResponsiveContainer width="100%" height={380}>
        <RadarChart data={data} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
          <PolarGrid stroke="#e5e5e5" />
          <PolarAngleAxis dataKey="dim" tick={{ fill: '#1f1f1f', fontSize: 11, fontFamily: 'Oswald, sans-serif' }} />
          <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fill: '#999', fontSize: 10 }} stroke="#e5e5e5" />
          <Radar name="Ist" dataKey="ist" stroke="#658a07" fill="#658a07" fillOpacity={0.55} />
          <Radar name="Soll" dataKey="soll" stroke="#1f1f1f" fill="#1f1f1f" fillOpacity={0.15} strokeDasharray="4 3" />
          <Tooltip contentStyle={{ background: '#fff', border: '1px solid #1f1f1f', fontSize: 12 }} />
        </RadarChart>
      </ResponsiveContainer>
      <ChartLegend items={[
        { color: '#658a07', label: 'Ist-Zustand (aktuelle Bewertung)' },
        { color: '#1f1f1f', label: 'Soll-Benchmark (reifer Mittelständler im Segment)', dashed: true },
      ]} />
      <SourceAnnotations
        items={annotations.map((a) => ({ name: a.label, source: a.dim.source, note: a.dim.note }))}
      />
    </ChartFrame>
  );
}


// =============================================================================
// Use-Case-Priorisierungs-Matrix (Bubble: Impact x Effort, Bubble-Size = Budget)
// =============================================================================

export function UsecaseBubble({ items }) {
  if (!items || items.length === 0) return null;
  // Mapping: X = effort (Komplexität), Y = impact (Geschäftswert), Z = budget
  const data = items.map((it) => ({
    name: it.name,
    x: it.effort,
    y: it.impact,
    z: Math.max(it.budget_keur || 5, 5), // minimal sichtbar
  }));

  return (
    <ChartFrame title="Use-Case-Priorisierung" subtitle="Geschäftswert (Y) gegen Umsetzungs-Komplexität (X). Bubble-Größe = geschätztes Budget in T€.">
      <ResponsiveContainer width="100%" height={380}>
        <ScatterChart margin={{ top: 20, right: 30, bottom: 50, left: 40 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
          <XAxis type="number" dataKey="x" domain={[0, 10]} name="Komplexität"
            label={{ value: 'Komplexität (0 = einfach, 10 = komplex)', position: 'insideBottom', offset: -10, fontSize: 11, fill: '#666' }}
            tick={{ fontSize: 11, fill: '#666' }} stroke="#999" />
          <YAxis type="number" dataKey="y" domain={[0, 10]} name="Impact"
            label={{ value: 'Geschäftswert / Impact', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666' }}
            tick={{ fontSize: 11, fill: '#666' }} stroke="#999" />
          <ZAxis type="number" dataKey="z" range={[200, 1400]} />
          <ReferenceLine x={5} stroke="#ccc" strokeDasharray="3 3" />
          <ReferenceLine y={5} stroke="#ccc" strokeDasharray="3 3" />
          <Tooltip cursor={{ strokeDasharray: '3 3' }}
            contentStyle={{ background: '#fff', border: '1px solid #1f1f1f', fontSize: 12 }}
            formatter={(value, name) => {
              if (name === 'Impact') return [`${value} / 10`, 'Impact'];
              if (name === 'Komplexität') return [`${value} / 10`, 'Komplexität'];
              return value;
            }}
            labelFormatter={() => ''}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <div style={{ background: '#fff', border: '1px solid #1f1f1f', padding: 8, fontSize: 12 }}>
                  <strong>{d.name}</strong><br />
                  Impact: {d.y} / 10<br />
                  Komplexität: {d.x} / 10<br />
                  Budget: ~{d.z} T€
                </div>
              );
            }} />
          <Scatter name="Use-Cases" data={data} fill="#658a07" fillOpacity={0.7}>
            <LabelList dataKey="name" position="top" style={{ fontSize: 10, fill: '#1f1f1f' }} />
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      <p className="text-[11px] text-gray-500 mt-2 italic">
        Quick-Wins liegen oben links (hoher Impact, niedrige Komplexität). Strategische Investments oben rechts. Vermeiden: unten rechts (hohe Komplexität, geringer Impact).
      </p>
      <SourceAnnotations
        items={items.filter((it) => isEstimateSource(it.source)).map((it) => ({ name: it.name, source: it.source, note: it.note }))}
      />
    </ChartFrame>
  );
}


// =============================================================================
// Datenbasis-Gap-Waterfall (Start, Verluste, Endwert)
// =============================================================================

export function DataGapWaterfall({ steps }) {
  if (!steps || steps.length === 0) return null;

  // Waterfall via gestackte Bars: pro Stufe ein "base" (unsichtbar) + ein "delta" (sichtbar).
  // Start- und End-Balken werden voll dargestellt, Verlust-Stufen schweben.
  let runningValue = 0;
  const data = steps.map((s) => {
    if (s.is_total) {
      const value = s.value ?? 0;
      runningValue = value;
      return { label: s.label, base: 0, delta: value, total: value, kind: 'total', display: value };
    }
    const delta = s.delta ?? 0; // typisch negativ
    const newValue = runningValue + delta;
    const base = Math.min(runningValue, newValue);
    const visible = Math.abs(delta);
    runningValue = newValue;
    return {
      label: s.label, base, delta: visible, total: runningValue,
      kind: delta < 0 ? 'loss' : 'gain', display: delta,
    };
  });

  const colors = { total: '#1f1f1f', loss: '#c2410c', gain: '#658a07' };

  return (
    <ChartFrame title="Datenbasis — von vorhanden zu KI-bereit" subtitle="Welcher Anteil Ihrer Daten ist heute KI-tauglich? Was geht durch Silos, Unstruktur und Compliance verloren?">
      <ResponsiveContainer width="100%" height={340}>
        <BarChart data={data} margin={{ top: 20, right: 30, bottom: 50, left: 30 }}>
          <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#1f1f1f', fontFamily: 'Oswald, sans-serif' }}
            interval={0} angle={-15} textAnchor="end" height={70} stroke="#999" />
          <YAxis tick={{ fontSize: 11, fill: '#666' }} stroke="#999"
            label={{ value: 'Daten-Anteil (%)', angle: -90, position: 'insideLeft', fontSize: 11, fill: '#666' }} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <div style={{ background: '#fff', border: '1px solid #1f1f1f', padding: 8, fontSize: 12 }}>
                  <strong>{d.label}</strong><br />
                  {d.kind === 'total' ? `${d.display} %` : `${d.display > 0 ? '+' : ''}${d.display} %`}<br />
                  <span style={{ color: '#888' }}>Lauf-Total: {d.total} %</span>
                </div>
              );
            }} />
          <Bar dataKey="base" stackId="a" fill="transparent" />
          <Bar dataKey="delta" stackId="a">
            {data.map((entry, i) => (<Cell key={i} fill={colors[entry.kind]} />))}
            <LabelList dataKey="display" position="top" style={{ fontSize: 11, fill: '#1f1f1f', fontWeight: 'bold' }}
              formatter={(v) => `${v > 0 ? '+' : ''}${v} %`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartLegend items={[
        { color: '#1f1f1f', label: 'Gesamt-Anteil (Start / Ende)' },
        { color: '#c2410c', label: 'Verlust durch Datenproblem' },
      ]} />
      <SourceAnnotations
        items={steps.filter((s) => isEstimateSource(s.source)).map((s) => ({ name: s.label, source: s.source, note: s.note }))}
      />
    </ChartFrame>
  );
}


// =============================================================================
// Hilfs-Komponenten (Frame + Legende, einheitliches Layout)
// =============================================================================

function ChartFrame({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-gray-200 p-5 md:p-6 mb-6">
      <h3 className="text-base md:text-lg font-bold text-[#1f1f1f] uppercase tracking-wider mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
        {title}
      </h3>
      {subtitle && <p className="text-xs text-gray-500 mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function ChartLegend({ items }) {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-[11px]">
      {items.map((it, i) => (
        <div key={i} className="inline-flex items-center gap-1.5">
          <span className={`inline-block w-3 h-3 ${it.dashed ? 'border-2 border-dashed' : ''}`} style={{ background: it.dashed ? 'transparent' : it.color, borderColor: it.color }} />
          <span className="text-gray-600">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// Listet alle Datenpunkte des Charts auf die NICHT aus Kunden-Angaben stammen.
// Macht transparent welche Werte Schätzungen sind und mit welcher Begründung.
function SourceAnnotations({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4 pt-3 border-t border-dashed border-gray-200">
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
        Quellen-Hinweise zu diesem Chart
      </p>
      <ul className="space-y-1">
        {items.map((it, i) => {
          const badge = SOURCE_BADGE[it.source] || SOURCE_BADGE.estimated;
          return (
            <li key={i} className="text-[11px] text-gray-600 flex items-start gap-2">
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded text-[10px] font-bold flex-shrink-0 ${badge.cls}`}>{badge.sym}</span>
              <span>
                <strong className="text-[#1f1f1f]">{it.name}:</strong>{' '}
                <span className="italic">{SOURCE_LABEL[it.source] || it.source}</span>
                {it.note && <> — {it.note}</>}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


// =============================================================================
// Wrap-Komponente — Disclaimer + alle drei Charts + Quellen-Footnote
// =============================================================================

export default function ReportCharts({ visuals }) {
  if (!visuals) return null;
  const disclaimer = visuals.confidence_disclaimer;
  const sources = visuals.sources_used || [];

  return (
    <div className="space-y-4">
      {disclaimer && (
        <div className="bg-amber-50 border border-amber-200 p-4">
          <p className="text-[10px] uppercase tracking-wider text-amber-800 font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Hinweis zur Datenbasis
          </p>
          <p className="text-xs text-amber-900 leading-relaxed">{disclaimer}</p>
        </div>
      )}

      <MaturityRadar maturity={visuals.maturity} />
      <UsecaseBubble items={visuals.usecase_matrix} />
      <DataGapWaterfall steps={visuals.data_gap_waterfall} />

      {sources.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 p-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Verwendete Quellen für Schätzungen
          </p>
          <ul className="space-y-1 text-[11px] text-gray-600">
            {sources.map((s, i) => <li key={i}>· {s}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
